// Modules
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import { TextField } from '@dasa-health/components-react'
import { SystemBoldViewon, SystemBoldViewoff, SystemOutlineWarning } from '@dasa-health/icons'
import { debounce } from 'lodash'
import { useLocation } from 'react-router-dom'

// Application
import { useSignUpAnaytics } from 'hooks/useSignUpAnaytics'
import { classNameCheck, passwordValidator, Response, WEAK_PASSWORD_ERROR } from 'utils'
import { CreatePasswordSchema } from './utils/validation'

// Components
import { SecurityBarStatus, ValidInformation } from 'components'
import { Container, PasswordCriteria, StyledForm, TextFieldContainer, Title, Text } from './styles'
import { SignUpFooter } from 'redesign/components/molecules/SignUpFooter'
import { UNICO_CONSTANTS } from 'redesign/constants'

export interface NewPasswordInitial {
    password: string
    confirmPassword: string
}
interface Props {
    hasMDM?: boolean
    initialValues: NewPasswordInitial
    handleSubmit: (value: NewPasswordInitial, setFieldError?: (field: string, value: string | undefined) => void) => Promise<void>
}

export const NewPasswordSimplified: React.FC<Props> = ({ initialValues, handleSubmit, hasMDM = false }) => {
    const [viewPassword, setViewPassword] = useState(false)
    const [helperText, setHelperText] = useState('')
    const [valid, setValid] = useState<Response>()
    const { dispatchAnalyticsEvent } = useSignUpAnaytics({ hasMDM })
    const [isLoading, setIsLoading] = useState(false)

    const biometyDataLS = JSON.parse(sessionStorage.getItem(UNICO_CONSTANTS.STORAGE) || '{}')

    const isUnicoRecoverFlow = biometyDataLS?.origin === UNICO_CONSTANTS.ORIGIN.RECOVER

    const { touched, dirty, errors, isValid, values, handleChange, handleBlur, setFieldError } = useFormik({
        initialValues,
        onSubmit: submitValues => {
            handleSubmit(submitValues, setFieldError)
        },
        validationSchema: CreatePasswordSchema,
    })
    const [variant, setVariant] = useState<string | undefined>(undefined)

    const { pathname } = useLocation()

    const [strongBarStatus, setStrongBarStatus] = useState<SecurityBarStatus | undefined>()

    const handleViewPassword = () => {
        setViewPassword(!viewPassword)
    }

    const verifyCriteria = useCallback(() => {
        const criteriaArray: string[] = []

        valid?.lowerValid && criteriaArray.push('lower')
        valid?.upperValid && criteriaArray.push('upper')
        valid?.numberValid && criteriaArray.push('number')
        valid?.specialCharValid && criteriaArray.push('specialChar')

        if (criteriaArray.length === 0) {
            return setStrongBarStatus(undefined)
        }
        if (!valid?.lengthValid || criteriaArray.length === 1) {
            return setStrongBarStatus(SecurityBarStatus.weak)
        }
        if (criteriaArray.length === 2 || criteriaArray.length === 3) {
            return setStrongBarStatus(SecurityBarStatus.medium)
        }
        if (criteriaArray.length === 4) {
            return setStrongBarStatus(SecurityBarStatus.strong)
        }
    }, [valid])

    const handlePasswordHelperText = useCallback(() => {
        if (values.password.length > 40 && strongBarStatus === SecurityBarStatus.weak) {
            return setHelperText(WEAK_PASSWORD_ERROR)
        }
        if (errors.password) {
            return setHelperText(errors.password)
        }
        if (strongBarStatus === SecurityBarStatus.weak) {
            return setHelperText(WEAK_PASSWORD_ERROR)
        }

        return setHelperText('')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errors.password, strongBarStatus])

    const nameEvent = (pathName: string) => {
        if (pathName === '/recuperar-senha') {
            return 'reset_password'
        }
        return 'cadastro_web'
    }

    const debouncedValidate = useMemo(
        () =>
            debounce(() => {
                setVariant(
                    strongBarStatus === SecurityBarStatus.weak ? 'error' : classNameCheck(touched.password, dirty, !errors.password),
                )
                handlePasswordHelperText()
            }, 100),

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [errors.password, strongBarStatus],
    )
    useEffect(() => {
        debouncedValidate()
    }, [values, debouncedValidate])

    useEffect(() => {
        verifyCriteria()
    }, [values.password, verifyCriteria])

    useEffect(() => {
        const updated = passwordValidator(values.password)
        setValid(updated)
    }, [values])

    return (
        <StyledForm>
            <form
                onSubmit={e => {
                    e.preventDefault()
                    setIsLoading(true)
                    handleSubmit(values, setFieldError)
                }}
            >
                <PasswordCriteria>
                    <ValidInformation
                        dirty={dirty}
                        text="Mínimo de 8 caracteres"
                        touched={touched.password}
                        valid={valid?.lengthValid}
                    />
                    <ValidInformation
                        dirty={dirty}
                        text="Letra maiúscula e/ou minúscula"
                        touched={touched.password}
                        valid={valid?.upperOrAndLower}
                    />
                    <ValidInformation
                        dirty={dirty}
                        text="Número e/ou caractere especial"
                        touched={touched.password}
                        valid={valid?.numberOrAndSpecialChar}
                    />
                </PasswordCriteria>

                <TextFieldContainer>
                    <TextField
                        className="textInput"
                        data-testid="password-input"
                        helperText={helperText}
                        id="password"
                        label="Insira a nova senha"
                        maxLength={41}
                        name="password"
                        onBlur={e => {
                            handleBlur(e)
                            dispatchAnalyticsEvent('passwordOnFieldBlur', {
                                name: nameEvent(pathname),
                                password: e.target.value,
                                errorMessage: helperText,
                                funnelCurrentStep: 'Crie uma senha',
                                password_is_valid: !Object.values(passwordValidator(e.target.value)).includes(false),
                                passwordStrength: strongBarStatus,
                            })
                        }}
                        onChange={handleChange}
                        trailingIcon={
                            viewPassword ? (
                                <SystemBoldViewoff data-testid="change-type-input" onClick={handleViewPassword} />
                            ) : (
                                <SystemBoldViewon data-testid="change-type-input" onClick={handleViewPassword} />
                            )
                        }
                        type={viewPassword ? 'text' : 'password'}
                        value={values.password}
                        variant={variant}
                    />
                </TextFieldContainer>
                {isUnicoRecoverFlow && (
                    <Container data-testid="warning-service-is-down-component">
                        <Title>
                            <SystemOutlineWarning color="#0037FF" fontSize="1.5rem" /> Reconhecimento facial
                        </Title>
                        <Text>
                            Pra concluir a atualização de cadastro, você vai realizar o reconhecimento facial e verificar sua
                            identidade com segurança.
                        </Text>
                    </Container>
                )}
                <SignUpFooter
                    isSubmitButtonDisabled={!dirty || !isValid || strongBarStatus === SecurityBarStatus.weak || isLoading}
                    isSubmitButtonLoading={isLoading}
                />
            </form>
        </StyledForm>
    )
}
